import React from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { Menu16, Plus16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, Header, Loading, Modal, SearchBox } from '@makyoapp/makyo-ui'

import { useAppContext } from '../context/AppContext'

/**
 * see `[LayoutProps]`
 */
interface HeaderBarProps {
  title: React.ReactNode
  icon?: React.ReactNode
  search?: string
  onSearch?: (search: string) => void
  onClearSearch?: () => void
  addNewModal?: React.ReactNode
  addNewModalTitle?: string
  onAddNewModalClose?: () => void
  addNewModalShow?: boolean
  addNewModalSetShow?: (value: boolean) => void
  actions?: React.ReactNode
  noDefaultClassName?: boolean
}

function HeaderBar({
  search,
  onSearch,
  onClearSearch,
  onAddNewModalClose,
  addNewModal,
  addNewModalTitle,
  addNewModalShow,
  addNewModalSetShow,
  actions,
  icon,
  title,
  noDefaultClassName,
}: HeaderBarProps) {
  const { toggleSlideMenu } = useAppContext()
  const { t } = useTranslation()

  return (
    <Header
      className='mk-header'
      title={typeof title === 'string' ? t(title) : title}
      //icon={<Ic tag={icon} width={16} height={16} />}
      noDefaultClassName={noDefaultClassName}
      defaultTextSize={false}
      icon={icon}
      textClassName={clsx(noDefaultClassName !== true && 'text-lg', noDefaultClassName === true && 'font-semibold')}
      before={
        <Button noPadding squared className='md:hidden!' onClick={toggleSlideMenu} variant={ButtonVariant.Ghost}>
          <Menu16 />
        </Button>
      }
    >
      <Header.Action className='items-center gap-2 w-full md:w-auto'>
        {onSearch && (
          <SearchBox
            id='search'
            noBorder={true}
            value={search}
            onSearch={onSearch}
            onClearSearch={onClearSearch}
            placeholder={t('Search')}
          />
        )}
        {actions}
        {addNewModal && (
          <>
            <Button
              icon={<Plus16 />}
              noPadding={true}
              onClick={() => addNewModalSetShow?.(true)}
              className='w-8 h-8 shrink-0'
            />
            <Modal
              show={addNewModalShow}
              onHide={() => {
                addNewModalSetShow?.(false)
                onAddNewModalClose?.()
              }}
            >
              <Modal.Header
                onCloseClick={() => {
                  addNewModalSetShow?.(false)
                  onAddNewModalClose?.()
                }}
              >
                {addNewModalTitle}
              </Modal.Header>
              {addNewModal}
            </Modal>
          </>
        )}
      </Header.Action>
    </Header>
  )
}
interface LayoutProps {
  title: React.ReactNode
  icon?: React.ReactNode
  //
  children?: React.ReactNode
  /**
   * Set to `false` will remove children container padding
   */
  noPadding?: boolean
  /**
   * Additional `ReactNode` actions after title
   */
  actions?: React.ReactNode
  /**
   * search
   */
  search?: string
  /**
   * add onSearch callback to include search actions
   */
  onSearch?: (search: string) => void
  /**
   * add onClearSearch callback to include clear search actions
   */
  onClearSearch?: () => void
  /**
   * fired when add new modal closed
   */
  onAddNewModalClose?: () => void
  /**
   * the modal content
   */
  addNewModal?: React.ReactNode
  /**
   * the modal title
   */
  addNewModalTitle?: string
  /**
   * the display state of the modal for adding new item or the [+] button
   */
  addNewModalShow?: boolean
  /**
   * set state of the modal for adding new item or the [+] button
   */
  addNewModalSetShow?: (value: boolean) => void
  /**
   * additional item before children content
   * use this to add additional items like inner page tab navigation
   */
  beforeContent?: React.ReactNode
  /**
   * content wrapper className
   */
  wrapClassName?: string
  /**
   * remove header text default class
   */
  headerNoDefaultClassName?: boolean
}

/**
 * This is a shared layout used by user/projects settings page
 * _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _
 * | [icon] [title]          [search][actions][add] |
 * | _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _
 * |                                                |
 * | [children]                                     |
 * |                                                |
 * |                                                |
 * _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _
 *
 * see `[LayoutProps]`
 **/
export default function Layout({
  title,
  icon,
  children,
  noPadding,
  actions,
  search,
  onSearch,
  onClearSearch,
  onAddNewModalClose,
  addNewModal,
  addNewModalTitle,
  addNewModalShow,
  addNewModalSetShow,
  beforeContent,
  wrapClassName,
  headerNoDefaultClassName,
}: LayoutProps) {
  return (
    <>
      <HeaderBar
        title={title}
        icon={icon}
        onAddNewModalClose={onAddNewModalClose}
        addNewModal={addNewModal}
        addNewModalTitle={addNewModalTitle}
        search={search}
        onSearch={onSearch}
        actions={actions}
        addNewModalShow={addNewModalShow}
        addNewModalSetShow={addNewModalSetShow}
        onClearSearch={onClearSearch}
        noDefaultClassName={headerNoDefaultClassName}
        //addNewModalDisplayState={addNewModalDisplayState}
      />
      {beforeContent}
      <div className={clsx(!noPadding && 'p-8', wrapClassName)}>{children}</div>
    </>
  )
}

export function LayoutFallback() {
  const { t } = useTranslation()

  return (
    <Layout title='FALLBACK'>
      <div>{t('Loading...')} FALLBACK</div>
    </Layout>
  )
}

export function LayoutLoading(props: LayoutProps) {
  return (
    <Layout {...props}>
      <Loading fullHeight />
    </Layout>
  )
}
